import React, { useEffect } from 'react'
import Layout from '../../components/Layout/Layout'
import { Button, Container, Form } from 'react-bootstrap'
import { Formik } from 'formik'
import * as Yup from 'yup'
import Link from 'next/link'
import { toast } from 'react-toastify'
import { getAmsLoginType } from '@srca-b2b/kp5-ui-common/utils/authAPIs'
import { getMaintenanceMode } from '@srca-b2b/kp5-ui-common/utils/generalAPIs'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useTranslation } from 'next-i18next'

import classNames from 'classnames'
import styles from './login.module.scss'
import PlainHeader from 'components/PlainHeader/PlainHeader'
import { correctPTlocale } from '@srca-b2b/kp5-ui-common/utils/helpers'
import getConfig from 'next/config'

const { publicRuntimeConfig } = getConfig()

const FederatedLogin = ({ loginTypeResponse, currentBaseUrl, validEmail = '', ...props }) => {
  const { t } = useTranslation('common')

  const schema = Yup.object().shape({
    email: Yup.string().email('Invalid email address').required('Required')
  })

  const handleError = async (data) => {
    // Show error messages when user is not AZURE_AD user.
    let fullMessage
    if (data !== 'AZURE_AD') {
      if (data === 'SAMSUNG_ACCOUNT') {
        fullMessage = (
          <div>
            <p className="toastTitle">{t('Identity_provider_not_set_up')}</p>
            {t('Use_your')}{' '}
            <Link href={`/en/login/samsung`}>
              <a>{t('Samsung_account_to_sign_in')}</a>
            </Link>{' '}
            {t('instead')}.
          </div>
        )
      } else {
        fullMessage = (
          <div>
            Account has not been setup, please{' '}
            <Link href={`/en/register`}>
              <a>register</a>
            </Link>
            .
          </div>
        )
      }
      toast.error(fullMessage ? fullMessage : data.message, { className: 'notification' })
    }
  }

  const onSubmit = async (formValues) => {
    const response = await getAmsLoginType(formValues.email)
    if (response.responseJSON === 'AZURE_AD') {
      window.location.href = getUAredirect(currentBaseUrl, formValues.email)
    } else {
      handleError(response.responseJSON)
    }
  }

  useEffect(() => {
    if (loginTypeResponse && loginTypeResponse.responseJSON !== 'AZURE_AD') {
      handleError(loginTypeResponse.responseJSON)
    }
  }, [])

  return (
    <Layout containerClassName="pt-0">
      <PlainHeader />
      <Container className={classNames(styles.sso)}>
        <div className={styles.content}>
          <h2 className={classNames(styles.heading, 'pageH2')}>{t('Sign_in')}</h2>
          <p className="font-weight-bold text-center mb-0">{t('Sign_in_desc_text')}</p>
          <Formik
            validationSchema={schema}
            onSubmit={onSubmit}
            initialValues={{
              email: validEmail
            }}
          >
            {({ handleSubmit, handleChange, values, errors, isSubmitting }) => (
              <Form noValidate onSubmit={handleSubmit}>
                <Form.Group controlId="ga-kp-SignIn-SSO-Email" className="mb-0">
                  <Form.Label>{t('Email')}</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={values.email}
                    onChange={(e) => {
                      handleChange(e)
                    }}
                    isInvalid={!!errors.email}
                    placeholder={'name@example.com'}
                  />
                </Form.Group>
                <Button
                  id="ga-kp-SignIn-SSO-Next"
                  className={classNames(styles.btnNext, 'btn-md')}
                  type="submit"
                  disabled={isSubmitting}
                >
                  {t('Next')}
                </Button>
              </Form>
            )}
          </Formik>
        </div>
      </Container>
    </Layout>
  )
}

const getUAredirect = (currentBaseUrl, email) => {
  return `${publicRuntimeConfig.uspUABaseUrl}?redirectUrl=${encodeURIComponent(
    `${currentBaseUrl}/en/auth-consume/ad`
  )}&loginType=AD&accountId=${encodeURIComponent(email)}&createSession=true`
}

export const getServerSideProps = async ({ req, query, locale }) => {
  // Redirect to maintenance page if maintenanceMode
  const maintenanceResponse = await getMaintenanceMode(req.headers.cookie)
  if (maintenanceResponse.statusCode == 200 && maintenanceResponse.data) {
    return {
      redirect: {
        destination: '/en/maintenance',
        permanent: false
      }
    }
  }

  const language = correctPTlocale(locale)
  const props = {
    ...(await serverSideTranslations(language, ['common']))
  }

  const currentBaseUrl = `https://${req.headers.host}`

  let queryEmail = query.email || ''
  let loginTypeResponse = null
  if (queryEmail) {
    loginTypeResponse = await getAmsLoginType(queryEmail)
    if (loginTypeResponse.responseJSON === 'AZURE_AD') {
      return {
        redirect: {
          destination: getUAredirect(currentBaseUrl, queryEmail),
          permanent: false
        }
      }
    }
  }

  return { props: { ...props, loginTypeResponse, validEmail: queryEmail, currentBaseUrl } }
}

export default FederatedLogin
